/**
 * The top bar.
 *
 * Everything here answers a question the user must never have to click for:
 *
 *  - Am I on real money? (`EnvironmentBadge`, red, shown only when LIVE)
 *  - Is data actually arriving? (connection pill, with a STALE state)
 *  - Is anything exposed without a stop? (unprotected-position counter)
 *  - Is the manual whole-application emergency stop armed? (kill-switch pill)
 *  - Can I stop everything right now? (the HALT button)
 *
 * The HALT button is never behind a menu and never scrolls away. It is the
 * one control this platform must always be able to accept.
 */

import { Menu, Octagon, RefreshCw, ShieldAlert, Zap, ZapOff } from 'lucide-react';
import { useState } from 'react';

import {
  POLL,
  useEnvironment,
  useKillSwitch,
  useManualHalt,
  useConnection,
} from '@/api/hooks';
import { ws } from '@/api/ws';
import { AccountMenu } from '@/auth/AccountMenu';
import { readKillSwitch } from '@/components/monitoring/entryGate';
import { protectionHeadline, protectionUnknownText } from '@/components/monitoring/protectionTruth';
import { useProtectionTruth } from '@/components/monitoring/useProtectionTruth';
import { cn } from '@/lib/cn';
import { demoNamesActive } from '@/lib/demoNames';
import { formatLocalTime } from '@/lib/format';
import { staleAfterMs } from '@/lib/liveQueryState';
import { useFreshness } from '@/lib/useFreshness';

import { BrandMark, BrandWordmark } from './BrandMark';
import { showsTopBarEnvironmentBadge } from './topBarEnvironment';
import { Badge, EnvironmentBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ConfirmModal } from '../ui/Modal';
import { toast } from '../ui/Toast';
import { Tooltip } from '../ui/Tooltip';
import { ThemeToggle, ThemeToggleCompact } from './ThemeToggle';

/**
 * Connection pill.
 *
 * `stale` is its own visible state and not merely "still connected". A
 * socket can be open while the server has stopped sending, and a frozen
 * price rendered as live is a money-losing bug rather than a cosmetic one.
 *
 * The healthy label is STREAM, never "LIVE". "LIVE" already means "this
 * process can move real money" two badges to the left, and one word with
 * two meanings in the same 40 pixels is how somebody reads a green feed
 * indicator as confirmation that they are on testnet.
 */
function ConnectionPill() {
  const connection = useConnection();

  const { tone, label, icon: Icon, pulse } =
    connection.status === 'open' && !connection.stale
      ? { tone: 'up' as const, label: 'STREAM', icon: Zap, pulse: true }
      : connection.stale
        ? { tone: 'danger' as const, label: 'STALE', icon: ShieldAlert, pulse: false }
        : connection.status === 'reconnecting'
          ? { tone: 'warn' as const, label: 'RECONNECTING', icon: RefreshCw, pulse: false }
          : connection.status === 'connecting'
            ? { tone: 'warn' as const, label: 'CONNECTING', icon: RefreshCw, pulse: false }
            : { tone: 'muted' as const, label: 'OFFLINE', icon: ZapOff, pulse: false };

  const silentFor =
    connection.lastMessageAt > 0
      ? Math.round((Date.now() - connection.lastMessageAt) / 1000)
      : null;

  return (
    <Tooltip
      side="bottom"
      content={
        <div className="space-y-0.5">
          <p className="font-semibold">Market data feed: {label}</p>
          {silentFor !== null && <p className="num text-mid">last message {silentFor}s ago</p>}
          {connection.dropped > 0 && (
            <p className="text-warn">
              {connection.dropped} message(s) dropped — this browser is not keeping up
            </p>
          )}
          {connection.error && <p className="text-danger">{connection.error}</p>}
          {connection.activeTopics.length > 0 && (
            <p className="num text-lo">{connection.activeTopics.join(', ')}</p>
          )}
          <p className="text-lo">Click to reconnect</p>
        </div>
      }
    >
      <button type="button" onClick={() => ws.reconnect()} className="inline-flex">
        <Badge tone={tone} size="sm" icon={Icon} uppercase>
          <span className={cn(pulse && 'pulse-live')}>{label}</span>
        </Badge>
      </button>
    </Tooltip>
  );
}

/**
 * Account-wide manual emergency stop. Strategy limits live on deployments.
 *
 * Read through `readKillSwitch`, the one kill-switch interpreter [mv2-D2], aged
 * at 14 s (two missed 5 s polls plus latency): an unreadable, failed or stale
 * read is "unknown", never "armed", and a switch last read as tripped stays
 * Halted, marked last known.
 */
function KillSwitchPill() {
  const query = useKillSwitch();
  const maxAgeMs = staleAfterMs(POLL.safety);
  const clock = useFreshness([{ dataUpdatedAt: query.dataUpdatedAt, maxAgeMs }]);
  const kill = readKillSwitch(query, { ...clock, maxAgeMs });
  const pill = kill.state === 'unknown' && kill.why === 'loading' ? 'checking' : kill.state;

  if (kill.state === 'tripped') {
    const data = kill.data;
    return (
      <Tooltip
        side="bottom"
        content={
          <div className="space-y-0.5">
            <p className="font-semibold text-danger">Kill-switch tripped</p>
            <p>{data?.trip_reason ?? data?.breach ?? 'Limit breached'}</p>
            {kill.lastKnown && (
              <p className="text-warn">Not refreshed since {formatLocalTime(kill.asOfMs)} — last known</p>
            )}
            <p className="text-lo">
              Re-arming is a deliberate, audited action in Monitoring › Limits — it does not clear on
              restart.
            </p>
          </div>
        }
      >
        <span className="inline-flex" data-killswitch-pill={pill}>
          <Badge tone="danger" icon={Octagon} uppercase>
            Halted
          </Badge>
        </span>
      </Tooltip>
    );
  }

  if (kill.state === 'unknown') {
    const checking = pill === 'checking';
    return (
      <Tooltip
        side="bottom"
        content={
          <div className="space-y-0.5">
            <p className="font-semibold">
              {checking ? 'Reading the emergency stop…' : 'Emergency stop state unknown'}
            </p>
            {kill.message !== null && <p className="text-mid">{kill.message}</p>}
            {kill.why === 'unreadable' && (
              <p className="text-mid">The server reports the kill-switch state as unreadable.</p>
            )}
            {!checking && (
              <p className="text-warn">Treat new risk as blocked until it answers — Monitoring › Limits.</p>
            )}
          </div>
        }
      >
        <span className="inline-flex" data-killswitch-pill={pill}>
          <Badge tone={checking ? 'muted' : 'warn'} size="sm">
            <span>Emergency stop</span>
            <span className="opacity-60">{checking ? 'checking…' : 'unknown'}</span>
          </Badge>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      side="bottom"
      content={
        <div className="space-y-0.5">
          <p className="font-semibold">Manual emergency stop armed</p>
          <p className="text-mid">
            Each strategy follows its frozen risk limits across all its markets.
          </p>
          <p className="text-lo">Use this switch only to halt the whole application.</p>
          {(kill.data?.warnings ?? []).map((warning) => (
            <p key={warning} className="text-warn">
              {warning}
            </p>
          ))}
        </div>
      }
    >
      <span className="inline-flex" data-killswitch-pill={pill}>
        <Badge tone="neutral" size="sm">
          <span>Emergency stop</span>
          <span className="opacity-60">armed</span>
        </Badge>
      </span>
    </Tooltip>
  );
}

/**
 * Unprotected-position counter.
 *
 * Renders nothing when the count is a KNOWN zero — this is not a metric to
 * glance at, it is an alarm. When it is non-zero it is the loudest thing in the
 * bar, because an open position without a resting stop is precisely the
 * situation this platform was built to make impossible. Checking reads
 * `protection …` and a failed or stale read reads `protection ?` — never a
 * silent zero — through the one protection truth [mv2-D2].
 */
function UnprotectedPill() {
  const p = useProtectionTruth();
  if (p.state === 'checking') {
    return (
      <Tooltip side="bottom" content="Reading exchange protection…">
        <span className="inline-flex" data-protection-pill="checking" data-protection-tone="muted">
          <Badge tone="muted" icon={ShieldAlert} uppercase>protection …</Badge>
        </span>
      </Tooltip>
    );
  }
  if (p.state === 'unknown') {
    const tone = (p.lastKnownCount ?? 0) > 0 ? 'danger' : 'warn';
    return (
      <Tooltip
        side="bottom"
        content={
          <div className="space-y-0.5">
            <p className="font-semibold">Protection state unknown</p>
            <p className="text-mid">{protectionUnknownText(p)}</p>
            <p className="text-lo">Check Monitoring › Reconciliation.</p>
          </div>
        }
      >
        <span className="inline-flex" data-protection-pill="unknown" data-protection-tone={tone}>
          <Badge tone={tone} icon={ShieldAlert} uppercase>protection ?</Badge>
        </span>
      </Tooltip>
    );
  }
  const count = p.count ?? 0;
  if (count === 0) return null;

  return (
    <Tooltip
      side="bottom"
      content={
        <div className="space-y-0.5">
          <p className="font-semibold text-danger">{protectionHeadline(count)}</p>
          <p>
            No verified exchange-resting stop covers the full size. Until one does, this exposure
            is naked even if this software keeps running.
          </p>
        </div>
      }
    >
      <span className="inline-flex" data-protection-pill="known" data-protection-tone="danger">
        <Badge tone="danger" icon={ShieldAlert} uppercase className="pulse-live">
          {count} unprotected
        </Badge>
      </span>
    </Tooltip>
  );
}

export interface TopBarProps {
  /** Opens the mobile drawer. Only rendered below `md`. */
  onOpenMenu?: () => void;
  /** Page-specific controls: symbol picker, timeframe, run selector. */
  children?: React.ReactNode;
}

export function TopBar({ onOpenMenu, children }: TopBarProps) {
  const { data: environment } = useEnvironment();
  const [haltOpen, setHaltOpen] = useState(false);

  const halt = useManualHalt({
    onSuccess: () => {
      setHaltOpen(false);
      toast.critical(
        'Everything halted',
        'All deployments are stopped and no new entries will be taken. Protective stops remain resting on the exchange.',
      );
    },
    onError: (error) => toast.error(error, 'Halt failed'),
  });

  return (
    <>
      <header className="flex h-[var(--density-topbar)] shrink-0 items-center gap-2 border-b border-border bg-surface-1 px-2 md:px-2.5">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          icon={Menu}
          aria-label="Open navigation"
          onClick={onOpenMenu}
          className="md:hidden"
        />

        <div className="flex shrink-0 items-center gap-2" aria-label="Bheema-PM">
          <span className="inline-flex sm:hidden">
            <BrandMark />
          </span>
          <span className="hidden sm:inline-flex">
            <BrandWordmark />
          </span>
          <span className="sr-only sm:hidden">Bheema-PM</span>
          {/* LIVE only (owner, 2026-09-25): see topBarEnvironment.ts. */}
          {environment && showsTopBarEnvironmentBadge(environment.environment) && (
            <span className="inline-flex" data-brand-env-badge>
              <EnvironmentBadge
                environment={environment.environment}
                venue={environment.venue}
              />
            </span>
          )}
          {/*
            The app trades and reads Binance USDⓈ-M perpetual futures everywhere
            (candle_store_dirname = candles_futures). This static badge makes the
            data source explicit on every screen. Spot data exists ONLY behind
            the Backtest Lab's manual data toggle; nothing else ever reads it.
          */}
          {/* Hidden while the demo names are on (owner, 2026-09-25; lib/demoNames). */}
          {!demoNamesActive() && (
            <span className="hidden min-[480px]:inline-flex" data-brand-futures-badge>
              <Badge
                tone="accent"
                size="sm"
                uppercase
                title="Every price, chart and backtest in this app uses Binance USDⓈ-M perpetual futures. Spot is available only via the Backtest Lab's manual data toggle."
              >
                USD-M Futures
              </Badge>
            </span>
          )}
        </div>

        {/* Page-supplied controls take the middle and are allowed to shrink. */}
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">{children}</div>

        {/*
          Responsive visibility is done with wrapper spans, never by adding
          `hidden` to a component's own className. Tailwind resolves
          conflicting display utilities by their order in the generated
          stylesheet, not by the order they appear in the class string, so a
          component that hardcodes `inline-flex` internally silently beats a
          `hidden` passed in from outside — which is how both theme toggles
          ended up rendering at once and pushing the bar off a phone screen.
        */}
        <div className="flex shrink-0 items-center gap-1.5">
          <UnprotectedPill />
          <span className="hidden lg:inline-flex">
            <KillSwitchPill />
          </span>
          <span className="hidden min-[480px]:inline-flex">
            <ConnectionPill />
          </span>

          {/*
            The one control that must always be reachable.

            Below `sm` the word collapses to the octagon to keep the bar on a
            phone screen, so the accessible name is carried by `aria-label`
            rather than by the (then absent) text — otherwise the single most
            important button in the app announces itself as "button".

            `min-h-11 min-w-11` gives it a 44px touch target on a phone, the
            platform minimum. It was 35x28: a miss-prone target on the
            control whose whole purpose is being hit correctly, first time,
            by someone who is not calm. Expressed as min-height and
            min-width rather than as height and width on purpose — those are
            different CSS properties, so they cannot lose a Tailwind
            specificity race with the `h-7` the `sm` size sets, which is
            exactly the trap this file already fell into once with the theme
            toggles.
          */}
          <Button
            variant="danger"
            size="sm"
            icon={Octagon}
            data-hotkey="halt"
            onClick={() => setHaltOpen(true)}
            aria-label="Halt all trading now"
            title="Stop every deployment now (Shift+H focuses this)"
            className="min-h-11 min-w-11 font-bold tracking-wide sm:min-h-0 sm:min-w-0"
          >
            <span className="hidden sm:inline">HALT</span>
          </Button>

          <span className="hidden sm:inline-flex">
            <ThemeToggle />
          </span>
          <span className="inline-flex sm:hidden">
            <ThemeToggleCompact />
          </span>

          {/* Who is signed in, and how to sign out. Renders nothing under the
              dev auth bypass (there is no session to end). */}
          <AccountMenu />
        </div>
      </header>

      <ConfirmModal
        open={haltOpen}
        onClose={() => setHaltOpen(false)}
        onConfirm={() => halt.mutate({ reason: 'Manual halt from the terminal top bar' })}
        title="Halt everything now?"
        tone="danger"
        confirmLabel="Halt all trading"
        loading={halt.isPending}
        message={
          <div className="space-y-2">
            <p>
              Every deployment stops and no new entries are taken. This is recorded in the audit
              log.
            </p>
            <p className="text-mid">
              Protective stops already resting on the exchange are <strong>not</strong> cancelled —
              they exist precisely so they outlive this software. Open positions stay open and
              stay protected.
            </p>
            <p className="text-mid">
              Re-arming afterwards is a separate, deliberate action and does not happen on
              restart.
            </p>
          </div>
        }
      />
    </>
  );
}
